import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AvailabilityGrid = ({ 
  selectedWeek, 
  availability, 
  onAvailabilityChange, 
  onBulkUpdate,
  conflictingShifts 
}) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [currentAvailabilityType, setCurrentAvailabilityType] = useState('available');
  const gridRef = useRef(null);

  const days = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const availabilityTypes = {
    available: { color: 'bg-success', label: 'Verfügbar', textColor: 'text-success-foreground' },
    unavailable: { color: 'bg-error', label: 'Nicht verfügbar', textColor: 'text-error-foreground' },
    preferred: { color: 'bg-primary', label: 'Bevorzugt', textColor: 'text-primary-foreground' },
    conditional: { color: 'bg-warning', label: 'Bedingt', textColor: 'text-warning-foreground' }
  };

  const getCellKey = (day, hour) => `${day}-${hour}`;

  const getCellAvailability = (day, hour) => {
    const key = getCellKey(day, hour);
    return availability?.[key] || 'unavailable';
  };

  const hasConflict = (day, hour) => {
    return conflictingShifts?.some(shift => 
      shift?.day === day && shift?.startHour <= hour && shift?.endHour > hour
    );
  };

  const handleMouseDown = (day, hour, event) => {
    event?.preventDefault();
    setIsSelecting(true);
    setSelectionStart({ day, hour });
    setSelectionEnd({ day, hour });
    setSelectedCells(new Set([getCellKey(day, hour)]));
  };

  const handleMouseEnter = (day, hour) => {
    if (isSelecting && selectionStart) {
      setSelectionEnd({ day, hour });
      updateSelection(selectionStart, { day, hour });
    }
  };

  const handleMouseUp = () => {
    if (isSelecting && selectedCells?.size > 0) {
      const updates = {};
      selectedCells?.forEach(cellKey => {
        updates[cellKey] = currentAvailabilityType;
      });
      onAvailabilityChange(updates);
    }
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setSelectedCells(new Set());
  };

  const updateSelection = (start, end) => {
    const newSelection = new Set();
    const startDay = days?.indexOf(start?.day);
    const endDay = days?.indexOf(end?.day);
    const startHour = Math.min(start?.hour, end?.hour);
    const endHour = Math.max(start?.hour, end?.hour);
    
    for (let dayIndex = Math.min(startDay, endDay); dayIndex <= Math.max(startDay, endDay); dayIndex++) {
      for (let hour = startHour; hour <= endHour; hour++) {
        newSelection?.add(getCellKey(days?.[dayIndex], hour));
      }
    }
    setSelectedCells(newSelection);
  };

  const handleKeyDown = (event) => {
    if (event?.key === 'Escape') {
      setIsSelecting(false);
      setSelectedCells(new Set());
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSelecting, selectedCells]);

  const formatHour = (hour) => {
    return `${hour?.toString()?.padStart(2, '0')}:00`;
  };

  const getWeekDateRange = () => {
    const startDate = new Date(selectedWeek);
    const endDate = new Date(selectedWeek);
    endDate?.setDate(endDate?.getDate() + 6);
    
    return `${startDate?.toLocaleDateString('de-DE')} - ${endDate?.toLocaleDateString('de-DE')}`;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-card">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Verfügbarkeitsraster</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Woche: {getWeekDateRange()}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={currentAvailabilityType}
              onChange={(e) => setCurrentAvailabilityType(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
            >
              {Object.entries(availabilityTypes)?.map(([key, type]) => (
                <option key={key} value={key}>{type?.label}</option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              iconName="Copy"
              iconPosition="left"
              iconSize={16}
              onClick={() => onBulkUpdate('copy')}
            >
              Woche kopieren
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {Object.entries(availabilityTypes)?.map(([key, type]) => (
            <div key={key} className="flex items-center space-x-2">
              <div className={`w-4 h-4 rounded ${type?.color}`}></div>
              <span className="text-muted-foreground">{type?.label}</span>
            </div>
          ))}
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded bg-destructive border-2 border-error"></div>
            <span className="text-muted-foreground">Konflikt</span>
          </div>
        </div>
      </div>
      {/* Grid */}
      <div className="p-6">
        <div className="overflow-x-auto">
          <div 
            ref={gridRef}
            className="grid grid-cols-8 gap-1 min-w-[800px]"
            style={{ gridTemplateColumns: '80px repeat(7, 1fr)' }}
          >
            {/* Header row */}
            <div className="p-2 text-center font-medium text-muted-foreground text-sm">
              Zeit
            </div>
            {days?.map(day => (
              <div key={day} className="p-2 text-center font-medium text-foreground text-sm bg-muted rounded">
                {day}
              </div>
            ))}

            {/* Time rows */}
            {hours?.map(hour => (
              <React.Fragment key={hour}>
                <div className="p-2 text-center text-sm text-muted-foreground font-mono bg-muted rounded flex items-center justify-center">
                  {formatHour(hour)}
                </div>
                {days?.map(day => {
                  const cellKey = getCellKey(day, hour);
                  const availabilityType = getCellAvailability(day, hour);
                  const isSelected = selectedCells?.has(cellKey);
                  const hasConflictHere = hasConflict(day, hour);
                  const typeConfig = availabilityTypes?.[availabilityType];

                  return (
                    <div
                      key={cellKey}
                      className={`
                        h-8 border border-border rounded cursor-pointer transition-all duration-150
                        ${typeConfig?.color}
                        ${isSelected ? 'ring-2 ring-ring' : ''}
                        ${hasConflictHere ? 'ring-2 ring-error' : ''}
                        hover:opacity-80
                      `}
                      onMouseDown={(e) => handleMouseDown(day, hour, e)}
                      onMouseEnter={() => handleMouseEnter(day, hour)}
                      title={`${day} ${formatHour(hour)} - ${typeConfig?.label}${hasConflictHere ? ' (Konflikt)' : ''}`}
                    >
                      {hasConflictHere && (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="AlertTriangle" size={12} color="white" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h3 className="font-medium text-foreground mb-2">Bedienungshinweise:</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Klicken und ziehen Sie, um Zeitblöcke auszuwählen</li>
            <li>• Halten Sie Strg gedrückt und klicken Sie für mehrere Auswahlen</li>
            <li>• Halten Sie Shift gedrückt und klicken Sie für Bereiche</li>
            <li>• Drücken Sie Escape, um die Auswahl abzubrechen</li>
            <li>• Rote Rahmen zeigen Konflikte mit bestehenden Schichten an</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityGrid;